import type mqtt from 'mqtt';
import MQTTClient, { type Options as MQTTClientProps } from './service-utils/mqtt-client';
import {
  SCAN_SESSION_MESSAGE_TYPES,
  type ScanSessionAcceptConnectionMessage,
  type ScanSessionAcceptDisconnectionMessage,
  type ScanSessionAcceptReadMessage,
  type ScanSessionFailReadMessage,
} from './types/trackers';

export interface ClientProps extends MQTTClientProps {
  subscriptionTopics?: Array<string>;
}

export default class RacemapMQTTClient {
  _host: string;
  _props: ClientProps;
  _client: MQTTClient;
  _subTopics: Array<string>;

  constructor(host: string, props: ClientProps = {}) {
    this._host = host;
    this._props = {
      ...props,
      host,
      onConnect: this._handleBrokerConnect,
    };
    this._client = new MQTTClient(this._props);
    this._subTopics = this._props.subscriptionTopics || [];
  }

  static async asAdmin(host: string, password: string | null = null) {
    const adminPassword = process.env.MQTT_ADMIN_PASSWORD || password;
    if (adminPassword == null || adminPassword === '') {
      throw new Error('Admin Password is invalid');
    }

    return await new RacemapMQTTClient(host, {
      username: 'admin',
      password: adminPassword,
    }).connect();
  }

  static async fromProps(host: string, props: ClientProps) {
    return await new RacemapMQTTClient(host, {
      ...props,
    }).connect();
  }

  static async fromEnvVars(props: ClientProps = {}) {
    const host = process.env.MQTT_HOST || process.env.HOST;
    if (host == null || host === '') {
      throw new Error('MQTT Host is invalid or missing');
    }
    const password = process.env.MQTT_PASSWORD;
    if (password == null || password === '') {
      throw new Error('MQTT Password is invalid or missing');
    }
    const username = process.env.MQTT_USERNAME;
    if (username == null || username === '') {
      throw new Error('MQTT Username is invalid or missing');
    }

    return await new RacemapMQTTClient(host, {
      ...props,
      username,
      password,
    }).connect();
  }

  static async fromWindow(props: ClientProps = {}) {
    const host = window.location.origin;
    const isSSL = host.startsWith('https://');

    return await new RacemapMQTTClient(host, {
      path: '/mqtt',
      protocol: isSSL ? 'wss' : 'ws',
      ...props,
    }).connect();
  }

  connect = async () => {
    await this._client.connect();
    return this;
  };

  disconnect = () => {
    this._client.end();
  };

  subscribe = async (topic: string, options: mqtt.IClientSubscribeOptions = { qos: 0 }) => {
    return this._client.subscribe(topic, options);
  };

  _publish = async (
    topic: string,
    message: Buffer | string,
    options: mqtt.IClientPublishOptions = { qos: 0 },
  ) => {
    return this._client.publish(topic, message, options);
  };

  sendScanSessionAcceptConnectMessage = async (readerId: string) => {
    const acceptConnectionMessage: ScanSessionAcceptConnectionMessage = {
      type: SCAN_SESSION_MESSAGE_TYPES.ACCEPT_CONNECTION,
    };

    return this._publish(`scan/${readerId}/reader`, JSON.stringify(acceptConnectionMessage));
  };

  sendScanSessionAcceptReadMessage = async (readerId: string, trackerId: string) => {
    const acceptReadMessage: ScanSessionAcceptReadMessage = {
      type: SCAN_SESSION_MESSAGE_TYPES.ACCEPT_READ,
      trackerId,
    };
    return this._publish(`scan/${readerId}/reader`, JSON.stringify(acceptReadMessage));
  };

  sendScanSessionReadFailMessage = async (readerId: string, trackerId: string, err = '') => {
    const failReadMessage: ScanSessionFailReadMessage = {
      type: SCAN_SESSION_MESSAGE_TYPES.FAIL_READ,
      trackerId,
      err,
    };
    return this._publish(`scan/${readerId}/reader`, JSON.stringify(failReadMessage));
  };

  sendScanSessionAcceptDisconnectMessage = async (readerId: string) => {
    const acceptDisconnectMessage: ScanSessionAcceptDisconnectionMessage = {
      type: SCAN_SESSION_MESSAGE_TYPES.ACCEPT_DISCONNECTION,
    };

    return this._publish(`scan/${readerId}/reader`, JSON.stringify(acceptDisconnectMessage));
  };

  _subscribeToTopics = async (subscriptionOptions: mqtt.IClientSubscribeOptions = { qos: 0 }) => {
    return Promise.all(this._subTopics.map((topic) => this.subscribe(topic, subscriptionOptions)));
  };

  _handleBrokerConnect = async () => {
    await this._subscribeToTopics();
  };

  get connected(): boolean {
    return this._client?.connected;
  }
}
